package com.neurobs.ccabserver.transcriptreview.util

import com.neurobs.ccab.casr.CasrDatabaseJSON.ReviewedTestTranscriptJSON
import com.neurobs.ccab.casr.EngineIdentifiers
import com.neurobs.ccabserver.transcriptreview.states.{RecordingTranscriptReviewData, TestTranscriptReviewData, TranscriptLabelList}
import com.neurobs.util.TimeSeconds


class TestTranscriptReviewHandler
(
	val sourceData: TestTranscriptReviewData,
	val recordings: Seq[RecordingTranscriptReviewHandler],
	var reviewedTestTranscriptId: Option[Long],
	val labelList: TranscriptLabelList
) {

	def toReviewedTestTranscriptJson(username: String, latency: Boolean): ReviewedTestTranscriptJSON = {
		val recordingTranscriptJson = {
			recordings
				.map(recordingReviewHandler => {
				recordingReviewHandler.reviewingTranscript.toReviewedTestTranscriptRecordingJson(recordingReviewHandler.code, recordingReviewHandler.recordingOffset, username, latency)
			})
		}
		ReviewedTestTranscriptJSON(recordingTranscriptJson)
	}

	def hasWordOverlap(): Boolean = {
		recordings.exists(_.hasWordOverlap())
	}

	def resolveWordOverlap(): Unit = {
		recordings.foreach(_.resolveWordOverlap())
	}
}

object TestTranscriptReviewHandler {

	def apply(sourceData: TestTranscriptReviewData, userId: Long): TestTranscriptReviewHandler = {
		val recordings = sourceData.recordings.map(RecordingTranscriptReviewHandler(_, userId))
		val reviewId = sourceData.serverData.selection.reviewed.find(_.userId == userId).map(_.reviewedTestTranscriptId)
		val labels = TranscriptLabelList(recordings)
		new TestTranscriptReviewHandler(sourceData, recordings, reviewId, labels)
	}
}

object RecordingTranscriptReviewHandler {

	def apply(sourceData: RecordingTranscriptReviewData, userId: Long): RecordingTranscriptReviewHandler = {
		val casrTranscript = NonEditableTranscript(sourceData.transcript.casrTranscript(), sourceData.audioSegment.interval)
		val asrTranscripts = sourceData.transcript.asrTranscripts().filter(_.engineIdentifier() != EngineIdentifiers.GOOGLE).map(NonEditableTranscript(_, sourceData.audioSegment.interval))
		val reviewedTranscripts: Seq[NonEditableTranscript] = sourceData.reviewedTranscripts.map(_._2).map(t => NonEditableTranscript(t, sourceData.audioSegment.interval))
		val reviewingTranscript =
			sourceData.reviewedTranscripts.find(_._1.userId == userId) match {
				case Some(rev) => EditableTranscript(rev._2.reviewedTranscript(), sourceData.audioSegment.interval)
				case None => EditableTranscript(sourceData.transcript.casrTranscript(), sourceData.audioSegment.interval)
			}
			// This is a previous approach that can populate with other people's reviews
			// but this was found to not actually be useful.
//			if (sourceData.reviewedTranscripts.isEmpty) EditableTranscript(sourceData.transcript.casrTranscript())
//				else sourceData.reviewedTranscripts.find(_._1.userId == userId) match {
//					case Some(rev) => EditableTranscript(rev._2.reviewedTranscript())
//					case None => sourceData.reviewedTranscripts.find(_._1.forLatency) match {
//						case Some(rev) => EditableTranscript(rev._2.reviewedTranscript())
//						case None => EditableTranscript(sourceData.reviewedTranscripts.head._2.reviewedTranscript())
//					}
//				}

		RecordingTranscriptReviewHandler(sourceData = sourceData, casrTranscript = casrTranscript, asrTranscripts = asrTranscripts, reviewedTranscripts = reviewedTranscripts, reviewingTranscript = reviewingTranscript)
	}
}

case class RecordingTranscriptReviewHandler
(
	sourceData: RecordingTranscriptReviewData,
	casrTranscript: NonEditableTranscript,
	asrTranscripts: Seq[NonEditableTranscript],
	reviewedTranscripts: Seq[NonEditableTranscript],
	reviewingTranscript: EditableTranscript
)
{
	val code = sourceData.sourceAudio.metadata.code
	val recordingOffset: TimeSeconds = sourceData.transcript.recordingOffset()

	//val recordingTranscriptExists = recordingTranscriptJson.isDefined
	//var hasBeenReviewedThisLoad: Boolean = false


	//def markAsReviewed(): Unit = {
	//  hasBeenReviewedThisLoad = true
	//}

	//val casrTranscript = recordingTranscriptJson.map(_.casrTranscript()).map(NonEditableTranscript(_)).getOrElse(NonEditableTranscript())
	//val asrTranscripts = recordingTranscriptJson.map(_.asrTranscripts().filter(_.engineIdentifier() != EngineIdentifiers.GOOGLE)).map(_.map(NonEditableTranscript(_))).getOrElse(List())

	val asrEngineIdentifiers = asrTranscripts.map(_.engineIdentifier)

//  var reviewedTranscript = reviewedRecordingTranscriptJson match {
//    case None => recordingTranscriptJson.map(_.casrTranscript()).map(EditableTranscript(_)).getOrElse(EditableTranscript())
//    case Some(transcriptJson) => EditableTranscript(transcriptJson.reviewedTranscript())
//  }

//  def promoteEngineTranscriptToReviewed(engineIdentifier: String) = {
//    findEngineTranscript(engineIdentifier).map(engineTranscript => {
//      reviewedTranscript = EditableTranscript(engineTranscript)
//    })
//  }

	def findEngineTranscript(engineIdentifier: String): Option[NonEditableTranscript] = {
		if (engineIdentifier == EngineIdentifiers.CASR) Some(casrTranscript)
		else asrTranscripts.find(_.engineIdentifier == engineIdentifier)
	}

	def addWord(newWord: EditableTranscriptWord): Unit = reviewingTranscript.addWord(newWord)

	def removeWord(word: EditableTranscriptWord): Unit = reviewingTranscript.removeWord(word)

	def indexOfWord(word: EditableTranscriptWord): Option[Int] = {
		reviewingTranscript.transcriptWords.indexOf(word) match {
			case index if (index >= 0) => Some(index)
			case _ => None
		}
	}

	def indexOfWord(word: NonEditableTranscriptWord, engineTranscript: NonEditableTranscript): Option[Int] = {
		engineTranscript.nonEditableTranscriptWords.indexOf(word) match {
			case index if (index >= 0) => Some(index)
			case _ => None
		}
	}

	def wordAtIndex(index: Int) = {
		reviewingTranscript.transcriptWords(index)
	}

	def findOverlappingWord(word: TranscriptWord, minOverlapFraction: Double): Option[EditableTranscriptWord] = {
		reviewingTranscript.transcriptWords.find(existingWord => word.overlapFractionWith(existingWord) >= minOverlapFraction)
	}

	def hasWordOverlap(): Boolean = {
		val transcriptWords = reviewingTranscript.transcriptWords
		transcriptWords.zipWithIndex.exists(zipped => {
			val (word, index) = zipped
			if (index > 0) {
				val prevWord = transcriptWords(index - 1)
				prevWord.overlapSecondsWith(word).positive()
			} else false
		})
	}

	def resolveWordOverlap(): Unit = {
		val transcriptWords = reviewingTranscript.transcriptWords
		transcriptWords.zipWithIndex.foreach(zipped => {
			val (word, index) = zipped
			if (index > 0) {
				val prevWord = transcriptWords(index - 1)
				resolveWordOverlap(prevWord, word)
			}
		})
	}

	def resolveWordOverlap(word1: EditableTranscriptWord, word2: EditableTranscriptWord, secondSpacing: TimeSeconds = TimeSeconds(0)): Unit = {
		if (word1.endTime > word2.startTime) {
			val secondsToTruncate = (word1.endTime - word2.startTime + secondSpacing) / 2
			word1.endTime = word1.endTime - secondsToTruncate
			word2.startTime = word2.startTime + secondsToTruncate
		}
	}

	def earliestWordStart: Option[TimeSeconds] = {

		val minAsrStartOpt = asrTranscripts.filter(_.transcriptWords.length > 0).sortBy(_.transcriptWords.head.startTime.seconds()).headOption.map(_.transcriptWords.head.startTime)
		val casrStartOpt = casrTranscript.transcriptWords.headOption.map(_.startTime)

		minAsrStartOpt match {
			case None => casrStartOpt
			case Some(minAsrStart) => casrStartOpt match {
				case None => minAsrStartOpt
				case Some(casrStart) => Some(minAsrStart.min(casrStart))
			}
		}
	}
}
